'use client';

import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FreePlanCard } from '@/components/pricing/FreePlanCard';
import { PaidPlanCard } from '@/components/pricing/PaidPlanCard';
import { useUserInfo } from '@/provider/UserInfoProvider';
import { usePricingModal } from '@/zustand/store';
import { posthogUTM, RPostHog } from '@/posthog';
import MicroPlanCard from '@/components/pricing/MicroPlanCard';
import PhotoRestorationPlanCard from '@/components/pricing/PhotoRestorationPlanCard';

interface GlobalPricingModalProps {
  asChild?: boolean;
  preventInteractOutside?: boolean;
  customClassName?: string;
}

export interface Credit {
  amount: string;
  label: string;
  pricePerImage?: number;
  monthlyPrice?: number;
  yearlyPrice?: number;
  monthlyPricePerImage?: number;
  yearlyPricePerImage?: number;
}

const { utm } = posthogUTM();
const GlobalPricingModal = ({}: GlobalPricingModalProps) => {
  const isPricingModalOpen = usePricingModal((s) => s.isPricingModalOpen);
  const setPricingModalOpen = usePricingModal((s) => s.setPricingModalOpen);
  const pricingModalContext = usePricingModal((s) => s.pricingModalContext);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isYearly, setIsYearly] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<string | null>('Get Pro');
  const [selectedCredits, setSelectedCredits] = useState<
    Record<string, Credit>
  >({
    'Get Pro': {
      amount: '120',
      label: 'Credits',
      monthlyPrice: 38.0,
      yearlyPrice: 19.0,
      monthlyPricePerImage: 0.32,
      yearlyPricePerImage: 0.16,
    },
    'Pay as You Go': {
      amount: '3',
      label: 'Credits',
      pricePerImage: 1.7,
      monthlyPrice: 5.0,
    },
    'Photo Restoration': {
      amount: '1',
      label: 'Credits',
      pricePerImage: 1.99,
      monthlyPrice: 1.99,
    },
  });

  // 处理积分选择
  const handleCreditSelect = (tierName: string, credit: Credit) => {
    setSelectedCredits((prev) => ({
      ...prev,
      [tierName]: {
        ...credit,
        // 确保 amount 是字符串类型
        amount: String(credit.amount),
      },
    }));
  };

  // 处理卡片选择
  const handlePlanSelect = (planName: string) => {
    // 直接设置选中的卡片，不允许取消选择
    setSelectedPlan(planName);
  };

  const { userIdentity, userInfo } = useUserInfo();
  const isLoginUser = userIdentity === 3;

  // 匿名用户且 credit 不为 0 或者匿名用户credit 为 0 且 action 是 downloadHD
  const isShowFreePlan =
    (userIdentity === 2 && userInfo?.plan.available_count! > 0) ||
    (userIdentity === 2 &&
      userInfo?.plan.available_count === 0 &&
      pricingModalContext?.action === 'downloadHD');
  // 匿名用户且 credit 为 0 且 action 是 operate 或者登录用户且 credit 为 0
  // 但是当module为'restore_image'时隐藏MicroPlanCard
  const isShowMicroPlan =
    ((userIdentity === 2 &&
      userInfo?.plan.available_count === 0 &&
      pricingModalContext?.action === 'operate') ||
      (userIdentity === 3 && userInfo?.plan.available_count === 0)) &&
    pricingModalContext?.module !== 'restore_image';

  // 当module为'restore_image'时显示PhotoRestorationPlanCard
  const isShowPhotoRestorationPlan =
    pricingModalContext?.module === 'restore_image';

  useEffect(() => {
    if (contentRef.current && isLoginUser) {
      contentRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [contentRef, isLoginUser]);

  // 动态生成积分配置
  const paidTiers: TypePaidTier[] = [
    {
      name: 'Get Pro',
      priceUnit: '/ credit',
      billing: 'Monthly',
      buttonText: 'Subscribe now',
      popular: true,
      features: [
        'Best Output Quality',
        'Priority Processing Speed',
        'Edit History Access',
        'No Hidden Fees',
        '100% Satisfaction Guarantee',
      ],
      credits: [
        {
          amount: '20',
          label: 'Credits',
          monthlyPrice: 10.0,
          yearlyPrice: 5.0,
          monthlyPricePerImage: 0.5,
          yearlyPricePerImage: 0.25,
        },
        {
          amount: '40',
          label: 'Credits',
          monthlyPrice: 18.0,
          yearlyPrice: 9.0,
          monthlyPricePerImage: 0.45,
          yearlyPricePerImage: 0.23,
        },
        {
          amount: '120',
          label: 'Credits',
          monthlyPrice: 38.0,
          yearlyPrice: 19.0,
          monthlyPricePerImage: 0.32,
          yearlyPricePerImage: 0.16,
        },
        {
          amount: '250',
          label: 'Credits',
          monthlyPrice: 58.0,
          yearlyPrice: 29.0,
          monthlyPricePerImage: 0.23,
          yearlyPricePerImage: 0.12,
        },
        {
          amount: '500',
          label: 'Credits',
          monthlyPrice: 98.0,
          yearlyPrice: 49.0,
          monthlyPricePerImage: 0.2,
          yearlyPricePerImage: 0.1,
        },
        {
          amount: '1000',
          label: 'Credits',
          monthlyPrice: 148.0,
          yearlyPrice: 74.0,
          monthlyPricePerImage: 0.15,
          yearlyPricePerImage: 0.08,
        },
      ] as Credit[],
    },
    {
      name: 'Pay as You Go',
      description: 'Use it when you need it',
      buttonText: 'Buy now',
      popular: false,
      features: [
        'Best Output Quality',
        'Priority Processing Speed',
        'No Hidden Fees',
        '100% Satisfaction Guarantee',
      ],
      credits: [
        // 固定第一个选项为3Credits
        {
          amount: '3',
          label: 'Credits',
          pricePerImage: 1.7,
          monthlyPrice: 5.0,
        },
        {
          amount: '40',
          label: 'Credits',
          pricePerImage: 0.48,
          monthlyPrice: 19.0,
        },
        {
          amount: '120',
          label: 'Credits',
          pricePerImage: 0.4,
          monthlyPrice: 49.0,
        },
        {
          amount: '250',
          label: 'Credits',
          pricePerImage: 0.35,
          monthlyPrice: 89.0,
        },
        {
          amount: '500',
          label: 'Credits',
          pricePerImage: 0.3,
          monthlyPrice: 149.0,
        },
        {
          amount: '1000',
          label: 'Credits',
          pricePerImage: 0.25,
          monthlyPrice: 249.0,
        },
      ] as Credit[],
    },
  ];

  useEffect(() => {
    const { reportInfo } = pricingModalContext;
    // 用户打开定价弹窗, 上报事件
    if (isPricingModalOpen) {
      RPostHog('user_open_pricing_modal', { ...reportInfo, ...utm });
    }

    // 用户关闭弹窗，上报事件
    if (!isPricingModalOpen && reportInfo) {
      RPostHog('user_close_pricing_modal', { ...reportInfo, ...utm });
    }
  }, [isPricingModalOpen, pricingModalContext]);

  return (
    <Dialog
      open={isPricingModalOpen}
      onOpenChange={(open) => setPricingModalOpen(open, pricingModalContext)}
    >
      <DialogContent
        className={`h-[100%] w-full max-w-none overflow-hidden rounded-none p-0 sm:h-[90vh] sm:rounded-2xl md:max-h-[95vh] md:w-4/5 md:p-0 ${
          isShowMicroPlan || isShowFreePlan || isShowPhotoRestorationPlan
            ? ''
            : 'max-w-4xl'
        }`}
      >
        <div
          className={`h-full overflow-y-auto p-4 [scrollbar-width:none] sm:p-6 md:p-8 [&::-webkit-scrollbar]:hidden`}
        >
          <div ref={contentRef} />
          {pricingModalContext?.topHeader}
          <DialogHeader>
            <DialogTitle className='font-helvetica-roman text-center text-2xl md:text-[32px]'>
              Unlock the Best Plan for You
            </DialogTitle>
            <DialogDescription className='font-helvetica-medium mx-auto mb-4 w-fit rounded-full bg-blue-50 px-4 py-2 text-sm text-blue-500'>
              Credits are used per tool based on complexity. Al Headshots
              require a separate plan.
            </DialogDescription>
          </DialogHeader>
          <div className='@container/pricing'>
            <div
              className={`mt-6 grid w-full gap-6 ${
                isShowMicroPlan || isShowFreePlan || isShowPhotoRestorationPlan
                  ? 'grid-cols-1 @3xl/pricing:grid-cols-3'
                  : 'grid-cols-1 @xl/pricing:grid-cols-2'
              }`}
            >
              {isShowFreePlan && <FreePlanCard />}

              {isShowMicroPlan && (
                <MicroPlanCard
                  _from={pricingModalContext?.reportInfo?.moduleName}
                  userIdentity={userIdentity}
                  onSelect={() => handlePlanSelect('Micro')}
                  cancelUrl={pricingModalContext?.cancel_url}
                  successRedirect={pricingModalContext?.success_redirect}
                />
              )}

              {isShowPhotoRestorationPlan && (
                <PhotoRestorationPlanCard
                  _from={
                    pricingModalContext?.reportInfo?.moduleName ||
                    'restore_image'
                  }
                  userIdentity={userIdentity}
                  selectedCredit={selectedCredits['Photo Restoration']}
                  isSelected={selectedPlan === 'Photo Restoration'}
                  onSelect={() => handlePlanSelect('Photo Restoration')}
                  cancelUrl={pricingModalContext?.cancel_url}
                  successRedirect={pricingModalContext?.success_redirect}
                />
              )}

              {paidTiers.map((tier) => (
                <PaidPlanCard
                  key={tier.name}
                  tier={tier}
                  _from={pricingModalContext?.reportInfo?.moduleName}
                  isYearly={isYearly}
                  selectedCredit={selectedCredits[tier.name]}
                  onCreditSelect={(credit) =>
                    handleCreditSelect(tier.name, credit)
                  }
                  userId={String(userIdentity)}
                  userIdentity={userIdentity}
                  onYearlyChange={setIsYearly}
                  cancelUrl={pricingModalContext?.cancel_url}
                  successRedirect={pricingModalContext?.success_redirect}
                  className={isLoginUser ? 'md:col-span-1' : 'md:col-span-1'}
                  onSelect={() => handlePlanSelect(tier.name)}
                />
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// 导出类型定义
export type TypePaidTier = {
  name: 'Get Pro' | 'Pay as You Go';
  popular?: boolean;
  priceUnit?: string;
  billing?: string;
  buttonText: 'Subscribe now' | 'Buy now';
  description?: string;
  features: string[];
  credits: Credit[];
};

// 更新价格格式化函数，移除小数部分
export function formatPrice(price: number | undefined) {
  if (price === undefined) return '$0';
  return `$${Math.floor(price) === price ? price : price.toFixed(2).replace(/\.?0+$/, '')}`;
}

export default GlobalPricingModal;
